import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { Dispatch, useEffect, useState } from 'react';
import Program from 'wwg-utilities/dist/collections/program';
import getProgramsRequest from '../../../lib/api/getProgramsRequest';
import { isSkillup, isWhereWeGo } from '../../../lib/config/project/project';
import LoadingCards from '../../shared/LoadingCards';
import LoadMoreButton from '../../shared/LoadMoreButton';
import NoResults from '../../shared/NoResults';
import TrainingCard from '../TrainingCard';

interface TrainingResultsProps {
  page: number;
  setPage: Dispatch<number>;
}

const TrainingResults: React.FC<TrainingResultsProps> = ({ page, setPage }) => {
  const { query } = useRouter();
  // This value is whether or not we have hit the last page of results
  // if so, we will hide the Load More button
  const [isComplete, setIsComplete] = useState(false);
  // The complete list of results
  const [results, setResults] = useState([] as Program[]);

  // The next fetched page of results. This reruns anytime the query changes (for filters or sorts) or the page number changes
  const {
    data: pageOfResults,
    isLoading,
    isFetching,
  } = useQuery(['training-results', page, query], () =>
    getProgramsRequest({
      ...query,
      page,
      limit: 20,
    }),
  );

  useEffect(() => {
    let newResults = [];

    if (pageOfResults) {
      if (page == 1) {
        setIsComplete(false); // Reset isComplete if we're on page 1
        newResults = pageOfResults; // Override old results by making new results just the new page of results
      } else {
        newResults = [...results, ...pageOfResults]; // if it's not page 1, we will keep the old results and spread in the new page of results at the end of the array
      }
      setResults(newResults);
      //If the results is not divisible by 20 (i.e. there were less than 20 results in the last fetch) we will mark this as complete and hide the load more button
      if (newResults.length % 20 != 0) {
        setIsComplete(true);
      }
    }
  }, [pageOfResults]);

  const nextPage = () => {
    // increase the page count
    setPage(page + 1);
  };
  return (
    <section className={`${isSkillup && 'py-4 md:py-16 bg-off-gray'}`}>
      <div
        className={`${
          isSkillup
            ? 'container relative grid grid-cols-1 gap-10 px-4 mx-auto results md:px-0 md:gap-x-10 md:gap-y-20 lg:gap-10 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'container relative grid grid-cols-1 gap-10 mx-auto results lg:gap-8 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
        }`}
      >
        {results?.map((program: any) => (
          <TrainingCard key={program.id} program={program}></TrainingCard>
        ))}
        {isFetching && <LoadingCards />}
      </div>
      {Boolean(!results?.length && !isLoading) && (
        <NoResults
          message={
            'No results for your query. Try removing the distance filter.'
          }
        />
      )}
      <div className="container mx-auto">
        <LoadMoreButton
          color={isWhereWeGo ? 'primary' : ''}
          isComplete={isComplete}
          nextPage={nextPage}
        />
      </div>
    </section>
  );
};

export default TrainingResults;
